import { useEffect, useState } from "react";
import { hitApi, dateFormatter, commonToast } from "../../components/commonAction";
import AffiliatePageLayout from "./AffiliatePageLayout";

export default function Payouts({ pageTitle }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [requesting, setRequesting] = useState(false);

    const load = () => {
        setLoading(true);
        hitApi({ url: "affiliate/payouts", method: "GET", data: {}, alert: false }, (resp) => {
            setLoading(false);
            if (resp?.status) setRows(resp.data?.payouts || []);
        });
        hitApi({ url: "affiliate/dashboard", method: "GET", data: {}, alert: false }, (resp) => {
            if (resp?.status) setStats(resp.data?.stats || null);
        });
    };
    useEffect(load, []);

    const requestPayout = () => {
        if (!window.confirm("Request a payout for your full available balance?")) return;
        setRequesting(true);
        hitApi({ url: "affiliate/payouts/request", method: "POST", data: {}, alert: false }, (resp) => {
            setRequesting(false);
            if (resp?.status) { commonToast("success", resp.message); load(); }
        });
    };

    const withdrawable = Number(stats?.withdrawableBalance || 0);
    const pending      = Number(stats?.pendingBalance || 0);
    const lifetime     = Number(stats?.currentBalance || 0);
    const minPayout    = Number(stats?.minimumPayoutAmount || 0);
    const negative     = lifetime < 0;
    const belowMin     = withdrawable < minPayout;
    const progressPct  = minPayout > 0 ? Math.min(100, Math.round((withdrawable / minPayout) * 100)) : 100;

    return (
        <AffiliatePageLayout pageTitle={pageTitle} heading="Payouts">
            {/* ── Balance hero ── */}
            {loading && <PayoutsBalanceSkeleton />}
            <div className="pxl-aff-balance" style={loading ? { display: 'none' } : undefined}>
                <div className="pxl-aff-balance-orb" />
                <div className="pxl-aff-balance-inner">
                    <div className="pxl-aff-balance-content">
                        <div className="pxl-aff-balance-label">Withdrawable now</div>
                        <div className={`pxl-aff-balance-amount${negative ? " is-negative" : ""}`}>
                            ${withdrawable.toFixed(2)}
                        </div>

                        <div className="pxl-aff-balance-meta">
                            <span><strong>${pending.toFixed(2)}</strong> in holdback</span>
                            <span className="pxl-aff-balance-sep">·</span>
                            <span><strong>${lifetime.toFixed(2)}</strong> lifetime balance</span>
                            {minPayout > 0 && (
                                <>
                                    <span className="pxl-aff-balance-sep">·</span>
                                    <span>min payout <strong>${minPayout.toFixed(2)}</strong></span>
                                </>
                            )}
                        </div>

                        {/* Progress toward minimum payout */}
                        {!negative && minPayout > 0 && belowMin && withdrawable > 0 && (
                            <div className="pxl-aff-balance-progress">
                                <div className="pxl-aff-balance-progress-bar">
                                    <div className="pxl-aff-balance-progress-fill" style={{ width: `${progressPct}%` }} />
                                </div>
                                <div className="pxl-aff-balance-progress-label">
                                    {progressPct}% of the way to your first payout
                                </div>
                            </div>
                        )}

                        {/* Status messages */}
                        {negative && (
                            <div className="pxl-aff-balance-note pxl-aff-balance-note--alert">
                                Negative lifetime balance from clawbacks. Earn it back before requesting a payout.
                            </div>
                        )}
                        {!negative && belowMin && withdrawable > 0 && (
                            <div className="pxl-aff-balance-note">
                                <strong>${(minPayout - withdrawable).toFixed(2)}</strong> short of the minimum. Earn more or wait for pending commissions to clear holdback.
                            </div>
                        )}
                        {!negative && withdrawable === 0 && pending > 0 && (
                            <div className="pxl-aff-balance-note">
                                All your earned commissions are in the holdback window. They'll become withdrawable automatically once the holdback period ends.
                            </div>
                        )}
                        {!negative && withdrawable === 0 && pending === 0 && (
                            <div className="pxl-aff-balance-note">
                                You haven't earned any commissions yet. Share your referral links to start earning — payouts are sent monthly via PayPal.
                            </div>
                        )}
                    </div>

                    {!negative && (
                        <button
                            className="pxlCommon-btn pxl-aff-balance-cta"
                            disabled={requesting || withdrawable <= 0 || belowMin}
                            onClick={requestPayout}
                            title={
                                withdrawable <= 0 ? "No withdrawable balance yet"
                                : belowMin ? `Below minimum payout of $${minPayout.toFixed(2)}`
                                : ""
                            }
                        >
                            {requesting ? "Requesting…" : <><IconWallet /> Request payout</>}
                        </button>
                    )}
                </div>
            </div>

            {/* Tip card */}
            {!negative && withdrawable === 0 && pending === 0 && (
                <div className="pxl-aff-tip">
                    <div className="pxl-aff-tip-icon"><IconBulb /></div>
                    <div>
                        <strong>Get started:</strong> Head to the <em>Links</em> tab to grab your referral URLs and share them with your audience.
                        Every paid subscription you refer earns you a commission, automatically.
                    </div>
                </div>
            )}

            {/* Payout history */}
            <div className="pxl-aff-panel">
                <div className="pxl-aff-panel-header">
                    <div>
                        <h3 className="pxl-aff-panel-title">Payout history</h3>
                        <div className="pxl-aff-panel-sub">Approved payouts are sent to your PayPal email on file.</div>
                    </div>
                    <span className="pxl-aff-panel-count">{rows.length}</span>
                </div>

                <div className="pxl-aff-table-wrap">
                    <table className="pxl-aff-table">
                        <thead>
                            <tr>
                                <th>Requested</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>PayPal txn</th>
                                <th>Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <>
                                    {[0, 1, 2, 3, 4].map(i => (
                                        <tr key={i}>
                                            <td><span className="pxl-sk" style={{ width: '70%', height: 13, display: 'block' }} /></td>
                                            <td><span className="pxl-sk" style={{ width: '50%', height: 13, display: 'block' }} /></td>
                                            <td><span className="pxl-sk" style={{ width: 60, height: 22, borderRadius: 11, display: 'block' }} /></td>
                                            <td><span className="pxl-sk" style={{ width: '55%', height: 13, display: 'block' }} /></td>
                                            <td><span className="pxl-sk" style={{ width: '65%', height: 13, display: 'block' }} /></td>
                                        </tr>
                                    ))}
                                </>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan="5" className="pxl-aff-table-state-cell">
                                    <EmptyState
                                        icon={<IconWallet />}
                                        title="No payouts yet"
                                        text="Once you've earned the minimum payout amount, you can request a withdrawal here. Every payout is processed within 7 business days."
                                    />
                                </td></tr>
                            ) : rows.map(r => (
                                <tr key={r._id}>
                                    <td><span className="pxl-aff-cell-strong">{dateFormatter(r.requestedAt)}</span></td>
                                    <td><span className="pxl-aff-amt">{r.currency} {Number(r.amount).toFixed(2)}</span></td>
                                    <td>
                                        {r.status === "paid"      && <span className="pxl-aff-pill pxl-aff-pill--success"><IconDot /> Paid</span>}
                                        {r.status === "approved"  && <span className="pxl-aff-pill pxl-aff-pill--info">Approved</span>}
                                        {r.status === "requested" && <span className="pxl-aff-pill pxl-aff-pill--muted">Requested</span>}
                                        {r.status === "rejected"  && <span className="pxl-aff-pill pxl-aff-pill--danger">Rejected</span>}
                                    </td>
                                    <td><code className="pxl-aff-cell-code">{r.externalPaypalTxId || "—"}</code></td>
                                    <td><span className="pxl-aff-cell-mute">{r.notes || ""}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </AffiliatePageLayout>
    );
}

function PayoutsBalanceSkeleton() {
    return (
        <div className="pxl-sk-panel" style={{ marginBottom: 20 }}>
            <div className="pxl-sk-panel-body">
                <span className="pxl-sk" style={{ width: 180, height: 40, display: 'block' }} />
                <span className="pxl-sk" style={{ width: 280, height: 11, display: 'block', marginTop: 10 }} />
                <span className="pxl-sk" style={{ width: 150, height: 40, borderRadius: 10, display: 'block', marginTop: 20 }} />
            </div>
        </div>
    );
}

function EmptyState({ icon, title, text }) {
    return (
        <div className="pxl-aff-empty">
            <div className="pxl-aff-empty-icon">{icon}</div>
            <div className="pxl-aff-empty-title">{title}</div>
            <p className="pxl-aff-empty-text">{text}</p>
        </div>
    );
}

/* ── Icons ─────────────────────────────────────────────────────────────────── */
function IconWallet() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12V7a2 2 0 0 0-2-2H5a2 2 0 0 0 0 4h16v5"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><circle cx="17" cy="14" r="1.5" fill="currentColor"/></svg>; }
function IconBulb()   { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18h6"/><path d="M10 22h4"/><path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1V18h6v-1.2c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2z"/></svg>; }
function IconDot()    { return <svg width="6" height="6" viewBox="0 0 6 6" fill="currentColor"><circle cx="3" cy="3" r="3"/></svg>; }
